// Assistant « Générer un document » (3 étapes + finalisation).
// Étape 1 : choix du modèle. Étape 2 : contexte affiché + parties cochées + directives.
// Étape 3 : édition TipTap — Valider est BLOQUÉ tant qu'il reste un marqueur [?...].
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, Sparkles, ArrowLeft, RefreshCw, Save } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useDossier } from '../../hooks/queries/useDossiers';
import { useDossierContacts } from '../../hooks/queries/useContacts';
import { useFeeAgreement } from '../../hooks/queries/useFeeAgreement';
import { FEE_AGREEMENT_MODE_LABELS } from '../../services/billing/feeAgreementService';
import {
  useDocumentTemplates, useGenerateDocument, useSaveDraft,
} from '../../hooks/queries/useDocumentGeneration';
import {
  markdownToTiptap, tiptapText, extractMarkers, type TiptapNode,
} from '../../services/documents/markdownDoc';
import type { DocumentDraft } from '../../services/documents/documentService';
import { DocumentEditor } from './DocumentEditor';
import { DocumentFinalizeStep } from './DocumentFinalizeStep';

const DISCLAIMER = 'Document généré par IA à titre d’assistance — relecture et validation par un avocat obligatoires avant tout usage.';

interface Props {
  dossierId: string;
  existingDraft: DocumentDraft | null;
  onClose: () => void;
  replaceFileId?: string | null;
  onRevisionSaved?: () => void;
}

/** content jsonb : soit { format:'markdown', md } (sortie EF), soit un doc TipTap (sauvegarde éditeur). */
function draftContentToDoc(content: unknown): TiptapNode | null {
  if (!content || typeof content !== 'object') return null;
  const c = content as { format?: string; md?: string; type?: string };
  if (c.format === 'markdown' && typeof c.md === 'string') return markdownToTiptap(c.md);
  if (c.type === 'doc') return c as TiptapNode;
  return null;
}

export function GenerateDocumentModal({ dossierId, existingDraft, onClose, replaceFileId, onRevisionSaved }: Props) {
  const collaborator = useAuthStore((s) => s.collaborator);
  const { data: templates } = useDocumentTemplates(true);
  const { data: dossier } = useDossier(dossierId);
  const { data: dossierContacts } = useDossierContacts(dossierId);
  const { data: fee } = useFeeAgreement(dossierId);
  const generate = useGenerateDocument();
  const saveDraft = useSaveDraft(dossierId);

  const initialDoc = existingDraft ? draftContentToDoc(existingDraft.content) : null;
  const [step, setStep] = useState<1 | 2 | 3 | 4>(existingDraft && initialDoc ? 3 : 1);
  const [templateId, setTemplateId] = useState<string | null>(existingDraft?.template_id ?? null);
  const [contactIds, setContactIds] = useState<Set<string>>(new Set());
  const [directives, setDirectives] = useState(existingDraft?.directives ?? '');
  const [draftId, setDraftId] = useState<string | null>(existingDraft?.id ?? null);
  const [title, setTitle] = useState(existingDraft?.title ?? '');
  const [doc, setDoc] = useState<TiptapNode | null>(initialDoc);
  const [docText, setDocText] = useState(initialDoc ? tiptapText(initialDoc) : '');
  // Clé de remontage de l'éditeur : changer de contenu (régénération) doit ré-initialiser TipTap.
  const [editorKey, setEditorKey] = useState(0);

  const markers = useMemo(() => extractMarkers(docText), [docText]);
  const template = (templates ?? []).find((t) => t.id === templateId) ?? null;

  // Bloc destinataire du PDF : première partie cochée (ordre = celui de la liste étape 2).
  // Aucune partie cochée (ex. reprise de brouillon) → tableau vide, le gabarit PDF s'en accommode.
  const recipientLines = useMemo(() => {
    const firstId = [...contactIds][0];
    if (!firstId) return [];
    const dc = (dossierContacts ?? []).find((d) => d.contact_id === firstId);
    if (!dc) return [];
    const c = dc.contact;
    const name = c.company_name ?? [c.first_name, c.last_name].filter(Boolean).join(' ');
    const town = [c.postal_code, c.city].filter(Boolean).join(' ');
    return [name, c.address_line1, c.address_line2, town].filter((l): l is string => !!l);
  }, [contactIds, dossierContacts]);

  const runGenerate = () => {
    if (!templateId) return;
    generate.mutate(
      { dossierId, templateId, directives, contactIds: [...contactIds], draftId: draftId ?? undefined },
      {
        onSuccess: (r) => {
          const d = markdownToTiptap(r.content_md);
          setDraftId(r.draft_id);
          setTitle(r.title);
          setDoc(d);
          setDocText(tiptapText(d));
          setEditorKey((k) => k + 1);
          setStep(3);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'La génération a échoué.'),
      },
    );
  };

  const persistDraft = (onDone?: () => void) => {
    if (!draftId || !doc) return;
    saveDraft.mutate(
      { id: draftId, patch: { title, content: doc } },
      {
        onSuccess: () => { toast.success('Brouillon enregistré.'); onDone?.(); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Échec de l’enregistrement.'),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-cime-modal>
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cime-primary" aria-hidden />
            <h2 className="font-heading text-lg font-semibold text-neutral-800">
              Générer un document {template ? `— ${template.name}` : ''}
            </h2>
          </div>
          <button type="button" className="text-neutral-400 hover:text-neutral-700 cursor-pointer" onClick={onClose} aria-label="Fermer">
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {step === 1 && (
            <div>
              <p className="mb-3 text-sm text-neutral-500">Choisissez le type de document à générer.</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(templates ?? []).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="rounded border border-neutral-200 p-4 text-left hover:border-cime-primary cursor-pointer"
                    onClick={() => { setTemplateId(t.id); setStep(2); }}
                  >
                    <p className="text-sm font-semibold text-neutral-800">{t.name}</p>
                    {t.description && <p className="mt-1 text-xs text-neutral-500">{t.description}</p>}
                  </button>
                ))}
                {(templates ?? []).length === 0 && (
                  <p className="text-sm text-neutral-500">Aucun modèle actif. Un administrateur peut en créer dans Paramètres → Modèles.</p>
                )}
              </div>
            </div>
          )}

          {step === 2 && dossier && (
            <div className="space-y-4">
              <div className="rounded border border-neutral-200 bg-neutral-50 p-3 text-sm">
                <p className="mb-1 font-medium text-neutral-700">Contexte injecté depuis le dossier</p>
                <p className="text-neutral-600">
                  {dossier.number} — {dossier.title}
                  {dossier.juridiction ? ` · ${dossier.juridiction}` : ''}
                  {dossier.rg_number ? ` · RG ${dossier.rg_number}` : ''}
                </p>
                {fee && <p className="text-neutral-600">Convention d’honoraires : {FEE_AGREEMENT_MODE_LABELS[fee.billing_mode]}</p>}
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-neutral-700">Parties concernées par le document</p>
                <ul className="space-y-1.5">
                  {(dossierContacts ?? []).map((dc) => {
                    const cid = dc.contact_id;
                    const name = dc.contact.company_name
                      ?? [dc.contact.first_name, dc.contact.last_name].filter(Boolean).join(' ');
                    return (
                      <li key={cid}>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
                          <input
                            type="checkbox"
                            checked={contactIds.has(cid)}
                            onChange={(e) => {
                              const next = new Set(contactIds);
                              if (e.target.checked) next.add(cid); else next.delete(cid);
                              setContactIds(next);
                            }}
                          />
                          <span>{name}{dc.role ? <span className="text-neutral-400"> — {dc.role}</span> : null}</span>
                        </label>
                      </li>
                    );
                  })}
                  {(dossierContacts ?? []).length === 0 && (
                    <li className="text-sm text-neutral-500">Aucune partie enregistrée sur ce dossier.</li>
                  )}
                </ul>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-neutral-700">Directives pour la rédaction</span>
                <textarea
                  rows={5}
                  className="mt-1 w-full rounded border border-neutral-200 px-3 py-2 text-sm"
                  placeholder="Ex. : mise en demeure de payer 4 500 € sous 15 jours, rappeler le courrier du 12 mai, ton ferme."
                  value={directives}
                  onChange={(e) => setDirectives(e.target.value)}
                />
              </label>

              <p className="text-xs text-neutral-500">{DISCLAIMER}</p>
            </div>
          )}

          {step === 3 && doc && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-neutral-500">Titre du document</span>
                <input
                  className="mt-0.5 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm font-medium"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              {markers.length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {markers.length} élément{markers.length > 1 ? 's' : ''} à compléter avant validation (surligné{markers.length > 1 ? 's' : ''} en jaune).
                </div>
              )}
              <DocumentEditor
                key={editorKey}
                initialDoc={doc}
                onUpdate={(d, text) => { setDoc(d); setDocText(text); }}
              />
              <p className="text-xs text-neutral-500">{DISCLAIMER}</p>
            </div>
          )}

          {step === 4 && doc && collaborator && (
            <DocumentFinalizeStep
              dossierId={dossierId}
              draftId={draftId!}
              title={title}
              doc={doc}
              recipientLines={recipientLines}
              templateId={existingDraft?.template_id ?? templateId}
              replaceFileId={replaceFileId}
              onDone={onClose}
              onRevisionSaved={onRevisionSaved}
              onBack={() => setStep(3)}
            />
          )}
        </div>

        {step !== 4 && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-5 py-3">
            <div>
              {step > 1 && (
                <button type="button"
                  className="flex items-center gap-1.5 rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 cursor-pointer"
                  onClick={() => setStep(step === 3 ? 2 : 1)}>
                  <ArrowLeft className="w-4 h-4" aria-hidden /> Retour
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {step === 2 && (
                <button type="button"
                  className="flex items-center gap-1.5 rounded bg-cime-primary px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
                  disabled={generate.isPending}
                  onClick={runGenerate}>
                  <Sparkles className="w-4 h-4" aria-hidden />
                  {generate.isPending ? 'Génération en cours…' : 'Générer'}
                </button>
              )}
              {step === 3 && (
                <>
                  <button type="button"
                    className="flex items-center gap-1.5 rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 disabled:opacity-50 cursor-pointer"
                    disabled={generate.isPending}
                    onClick={runGenerate}
                    title="Régénérer avec les mêmes directives">
                    <RefreshCw className="w-4 h-4" aria-hidden /> Régénérer
                  </button>
                  <button type="button"
                    className="flex items-center gap-1.5 rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 disabled:opacity-50 cursor-pointer"
                    disabled={saveDraft.isPending || !draftId}
                    onClick={() => persistDraft()}>
                    <Save className="w-4 h-4" aria-hidden /> Enregistrer le brouillon
                  </button>
                  <button type="button"
                    className="rounded bg-cime-primary px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
                    disabled={markers.length > 0 || !draftId}
                    title={markers.length > 0 ? 'Complétez les éléments surlignés avant de valider' : undefined}
                    onClick={() => persistDraft(() => setStep(4))}>
                    Valider
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
